import { rollWeighted, SeededRng } from '../seeded-rng';
import type { ItemRarity } from './game';

/**
 * How much unharvested Veil resonance has condensed in a given Crucible
 * manifestation at the moment it opened. Assigned randomly per crucible
 * (see `rollVeilSaturation`) rather than derived from party size — party
 * size no longer gates anything about a crucible. Drives runtime difficulty:
 * `threatMultiplier` stacks on top of the existing party-power-based floor
 * (`resolveTrialThreatMultiplier` in `player-power.ts`), uncompensated, in
 * the `CrucibleHost` constructor. `selectNpcVariantForPartyPower` in
 * run-content-resolver.ts is a separate, unrelated mechanism (NPC variant
 * selection by party power, not saturation).
 */
export type VeilSaturationId = 'faint' | 'stirring' | 'charged' | 'overflowing';

/**
 * A named saturation band. `essencePoolMin`/`essencePoolMax` mirror the
 * documented Crucible essence-pool sizes in
 * docs/gameplay/01-Essence-and-Crystals.md ("Crucible Total Essence"):
 * Small/Medium/Large/Boss. `modifiers` are display-only today — not yet
 * consumed by crucible-host's NPC spawn/threat logic, same status the old
 * difficulty-tier modifiers had.
 */
export interface VeilSaturationBandDefinition {
    readonly id: VeilSaturationId;
    readonly displayName: string;
    readonly essencePoolMin: number;
    readonly essencePoolMax: number;
    /** Relative weight for `rollVeilSaturation` — not a percentage. */
    readonly rollWeight: number;
    /** Inclusive range for how many affixes a crucible at this band rolls. */
    readonly affixCountMin: number;
    readonly affixCountMax: number;
    readonly modifiers: readonly string[];
    /**
     * Multiplier stacked on top of the party-power-based threat floor,
     * uncompensated — see `resolveVeilSaturationThreatMultiplier`. First-pass
     * placeholder reusing this band's own "NPC density" flavor percentages,
     * not tuned game balance.
     */
    readonly threatMultiplier: number;
    /**
     * Flat essence-value bonus (converted via `ESSENCE_EXCHANGE_RATE`) paid
     * equally to every player on a full clear, on top of what they
     * personally collected. First-pass placeholder (~15% of this band's
     * pool midpoint), not tuned game balance.
     */
    readonly fullClearBonusValue: number;
    /**
     * How many NPCs may be mid-attack against the *same player* at once - the
     * attack-token budget (see `AttackTokenService` in `@sorrem/crucible-host`).
     * Everything else in range still closes in, circles, and waits its turn.
     *
     * This is the knob that makes being outnumbered readable instead of
     * instantly lethal. Without a cap, N attackers deal N times the damage
     * simultaneously, so the only way to keep a group survivable is to nerf
     * each enemy until it is individually harmless. Capping concurrency
     * instead keeps single enemies threatening while making crowds a
     * positioning problem, and it is what gives Saturation real mechanical
     * teeth rather than being a reward multiplier with flavor text.
     *
     * Per-player, not per-crucible: an eight-player party should face eight
     * players' worth of pressure, not have the whole group protected by one
     * shared budget.
     */
    readonly concurrentAttackersPerPlayer: number;
    /**
     * The combat readiness (1-10, `resolvePlayerPower`) a character should
     * have for this band; `max` null means "and up" (#206). First-pass
     * ranges, not tuned balance.
     */
    readonly recommendedReadiness: { readonly min: number; readonly max: number | null };
    /**
     * Whether a party wipe here costs every member a carried item, whatever
     * their readiness. Otherwise it costs only members above
     * `recommendedReadiness.max` - see `resolveDefeatItemLoss`.
     */
    readonly isItemTakenFromEveryoneOnDefeat: boolean;
    /**
     * The chance (0..1) that the Veil joins an enemy's dropped item with the
     * essence orb from the same kill for the same player (#259,
     * `veil-transmutation.ts`). First-pass, to tune.
     */
    readonly transmutationChance: number;
    /**
     * The rarest an item that joining can make it: it only happens to items
     * below this. Never 'legendary' or 'mythic' (named items).
     */
    readonly transmutationRarityCap: Exclude<ItemRarity, 'legendary' | 'mythic'>;
    /**
     * The rarest item anything here can give: enemy drops, guardians, caches
     * and the heart's last echo. 'legendary' lets Unforgotten items drop;
     * Empyreal items never drop.
     */
    readonly maxItemRarity: Exclude<ItemRarity, 'mythic'>;
    /**
     * A treasure chest's loot here (#271): every item is at least this rare
     * (within `maxItemRarity`), and every tier above Hollow rolls this many
     * times as often. A richer Crucible's chest holds better loot. Chests
     * never give named items. First-pass, to tune.
     */
    readonly chestMinRarity: Exclude<ItemRarity, 'legendary' | 'mythic'>;
    readonly chestRarityBoost: number;
}

export const VEIL_SATURATION_BANDS: readonly VeilSaturationBandDefinition[] = [
    {
        id: 'faint',
        displayName: 'Faint',
        essencePoolMin: 200,
        essencePoolMax: 300,
        rollWeight: 45,
        affixCountMin: 0,
        affixCountMax: 1,
        modifiers: ['Standard threat', 'One attacker at a time'],
        threatMultiplier: 1.0,
        fullClearBonusValue: 40,
        concurrentAttackersPerPlayer: 1,
        recommendedReadiness: { min: 1, max: 3 },
        isItemTakenFromEveryoneOnDefeat: false,
        transmutationChance: 0.1,
        transmutationRarityCap: 'uncommon',
        maxItemRarity: 'uncommon',
        chestMinRarity: 'common',
        chestRarityBoost: 2,
    },
    {
        id: 'stirring',
        displayName: 'Stirring',
        essencePoolMin: 400,
        essencePoolMax: 600,
        rollWeight: 30,
        affixCountMin: 1,
        affixCountMax: 2,
        modifiers: ['+15% NPC density', 'Elite pack chance', 'Two attackers at once'],
        threatMultiplier: 1.15,
        fullClearBonusValue: 80,
        concurrentAttackersPerPlayer: 2,
        recommendedReadiness: { min: 3, max: 5 },
        isItemTakenFromEveryoneOnDefeat: false,
        transmutationChance: 0.1,
        transmutationRarityCap: 'rare',
        maxItemRarity: 'rare',
        chestMinRarity: 'common',
        chestRarityBoost: 3,
    },
    {
        id: 'charged',
        displayName: 'Charged',
        essencePoolMin: 800,
        essencePoolMax: 1200,
        rollWeight: 18,
        affixCountMin: 2,
        affixCountMax: 2,
        modifiers: ['+35% NPC density', 'Guaranteed elite pack', 'Three attackers at once'],
        threatMultiplier: 1.35,
        fullClearBonusValue: 160,
        concurrentAttackersPerPlayer: 3,
        recommendedReadiness: { min: 5, max: 7 },
        isItemTakenFromEveryoneOnDefeat: false,
        transmutationChance: 0.1,
        transmutationRarityCap: 'epic',
        maxItemRarity: 'epic',
        chestMinRarity: 'uncommon',
        chestRarityBoost: 3,
    },
    {
        id: 'overflowing',
        displayName: 'Overflowing',
        essencePoolMin: 1500,
        essencePoolMax: 2000,
        rollWeight: 7,
        affixCountMin: 2,
        affixCountMax: 3,
        modifiers: ['+35% NPC density', 'Guaranteed elite pack', 'Guaranteed rare drop', 'Four attackers at once'],
        threatMultiplier: 1.5,
        fullClearBonusValue: 300,
        concurrentAttackersPerPlayer: 4,
        recommendedReadiness: { min: 8, max: null },
        isItemTakenFromEveryoneOnDefeat: true,
        transmutationChance: 0.1,
        transmutationRarityCap: 'epic',
        maxItemRarity: 'legendary',
        chestMinRarity: 'uncommon',
        chestRarityBoost: 4,
    },
];

/** A recommended readiness range as players read it: "3–5", or "8+" with no top (#206). */
export function formatRecommendedReadiness(range: VeilSaturationBandDefinition['recommendedReadiness']): string {
    return range.max === null ? `${range.min}+` : `${range.min}–${range.max}`;
}

/**
 * The band to recommend at a character's readiness: the highest whose
 * minimum they meet, since neighboring ranges share their edges (3, 5).
 * Below every minimum, the faintest.
 */
export function resolveRecommendedVeilSaturation(readiness: number): VeilSaturationBandDefinition {
    return VEIL_SATURATION_BANDS.reduce(
        (recommended, band) => (readiness >= band.recommendedReadiness.min ? band : recommended),
        resolveVeilSaturationById('faint'),
    );
}

export function isVeilSaturationId(value: unknown): value is VeilSaturationId {
    return VEIL_SATURATION_BANDS.some((band) => band.id === value);
}

export function resolveVeilSaturationById(id: VeilSaturationId): VeilSaturationBandDefinition {
    const found = VEIL_SATURATION_BANDS.find((band) => band.id === id);
    if (!found) {
        throw new Error(`Unknown VeilSaturationId: ${id}`);
    }
    return found;
}

/**
 * Why a party wipe takes one of a member's carried items: they were above
 * the band's recommended readiness, or the band takes one from everyone.
 */
export type DefeatItemLossReason = 'above-band' | 'every-member';

/**
 * Whether a party wipe in this band costs a member with this readiness a
 * carried item, and why; undefined when it costs them nothing. Judged per
 * member: a strong character in a faint Crucible pays, a weak one in a
 * saturated Crucible does not - except where the band takes from everyone.
 */
export function resolveDefeatItemLoss(id: VeilSaturationId, readiness: number): DefeatItemLossReason | undefined {
    const band = resolveVeilSaturationById(id);
    if (band.isItemTakenFromEveryoneOnDefeat) {
        return 'every-member';
    }
    const { max } = band.recommendedReadiness;
    return max !== null && readiness > max ? 'above-band' : undefined;
}

/**
 * The threat multiplier this Saturation band stacks on top of the
 * party-power-based threat floor, uncompensated — see the constructor of
 * `CrucibleHost` (`packages/crucible-host`).
 */
export function resolveVeilSaturationThreatMultiplier(id: VeilSaturationId): number {
    return resolveVeilSaturationById(id).threatMultiplier;
}

/**
 * How much richer in essence a Crucible of this band is than a Faint one:
 * the middle of its essence pool over the middle of Faint's (about 1, 2, 4
 * and 7). Kill essence follows the pool, so rewards scaled by this keep pace
 * with it (a broken prop's orb, #302).
 */
export function resolveVeilSaturationEssenceScale(id: VeilSaturationId): number {
    const middle = (band: VeilSaturationBandDefinition): number => (band.essencePoolMin + band.essencePoolMax) / 2;
    return middle(resolveVeilSaturationById(id)) / middle(resolveVeilSaturationById('faint'));
}

/**
 * How many NPCs this Saturation band lets attack one player simultaneously.
 * See `concurrentAttackersPerPlayer`.
 */
export function resolveConcurrentAttackersPerPlayer(id: VeilSaturationId): number {
    return resolveVeilSaturationById(id).concurrentAttackersPerPlayer;
}

/**
 * Rolls a random Veil Saturation band, weighted toward lower saturations
 * being more common (see `rollWeight` on each band). This weighting is an
 * explicit first-pass placeholder, not tuned game balance — same status as
 * the power-to-tier mapping in `selectNpcVariantForPartyPower`
 * (run-content-resolver.ts).
 */
export function rollVeilSaturation(rng: SeededRng): VeilSaturationId {
    return (rollWeighted(VEIL_SATURATION_BANDS, (band) => band.rollWeight, rng) ?? VEIL_SATURATION_BANDS[VEIL_SATURATION_BANDS.length - 1]!).id;
}

/**
 * Rolls the one concrete essence-pool total for a run, within this
 * Saturation band's `essencePoolMin`/`essencePoolMax` range. Every placed
 * NPC's resolved essence value is a share of this same total — see
 * `RunContentResolver.loadRunContent` in
 * `packages/server/src/crucible/content/run-content-resolver.ts`.
 */
export function rollEssencePoolTotal(veilSaturation: VeilSaturationId, rng: SeededRng): number {
    const band = resolveVeilSaturationById(veilSaturation);
    return rng.randomInt(band.essencePoolMin, band.essencePoolMax + 1);
}
